import Link from 'next/link';
import { IoBuildOutline, IoBusinessOutline, IoSearch } from 'react-icons/io5';
import { TbCpu, TbGraph, TbPlug, TbRocket } from 'react-icons/tb';

import { Box, Button, ButtonProps, Group } from '@mantine/core';

import { FeatureShowcase } from '@marketing/components/feature-showcase/FeatureShowcase';
import { Feature, features } from '@marketing/data/features';

import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { Dashboard } from 'components/homepage/components/dashboard/Dashboard';
import { Hero } from 'components/homepage/components/Hero';
import { HeroCard } from 'components/homepage/components/HeroCard';
import { DCIDE } from 'components/logo/DCIDE';
import { DCIDEController } from 'components/logo/DCIDEController';
import { Page } from 'components/page/Page';
import { useCurrentUser } from 'hooks/use-current-user';
import { useHeroMedia } from 'hooks/use-hero-media';

import batteryPageImage from '../../../../app/(app)/controller/battery_page.png';
import controllerImage from '../../../../app/(app)/controller/controller_bg.png';
import liveDataImage from '../../../../app/(app)/controller/live_data.png';
import natsImage from '../../../../app/(app)/controller/nats_overview.png';
import productCatalogImage from '../../../../app/(app)/resourcepro/images/product-catalog.jpg';
import professionalsImage from '../../../../app/(app)/resourcepro/images/professionals.jpg';
import profileImage from '../../../../app/(app)/resourcepro/images/profile.jpg';

export const MoreInfoButton = (props: ButtonProps & { href: string }) => (
    <Box
        className="animation-border-spin"
        style={{
            '--background-color': 'var(--mantine-color-green-1)',
            '--animation-color': 'var(--mantine-color-cyan-1)',
            '--border-width': '4px',
            borderRadius: 6,
            alignSelf: 'center',
        }}
    >
        <Button component="a" size="lg" {...props} />
    </Box>
);

const controllerFeatures: Feature[] = [
    {
        title: 'DCIDE Controller',
        description:
            'The DCIDE Controller is your multivendor DC Microgrid controller, slashing time to power with rapid integration and real-time optimization.',
        icon: <TbCpu strokeWidth={1.25} />,
        preview: { type: 'image', image: controllerImage },
    },
    {
        title: 'Intelligent Energy Control',
        description:
            'Advanced energy management algorithms designed to optimize energy use, reduce costs, and enhance the reliability of your microgrid. Peak shaving, energy arbitrage, and self-consumption algorithms out of the box.',
        icon: <TbRocket strokeWidth={1.25} />,
        preview: { type: 'image', image: batteryPageImage },
    },
    {
        title: 'Real-time Monitoring',
        description:
            "Track power and energy flow with live data and historical analysis. Receive instant alerts for device or system-level faults to ensure system's uptime availability. Manage your microgrid from anywhere with secure remote access.",
        icon: <TbGraph strokeWidth={1.25} />,
        preview: { type: 'image', image: liveDataImage },
    },
    {
        title: 'Custom Application Development',
        description:
            'Take full control of your microgrid by developing your own application. Create a fully customized Energy Management System (EMS) in Python, enhanced with AI and integrated directly with the controller runtime.',
        icon: <TbPlug strokeWidth={1.25} />,
        preview: { type: 'image', image: natsImage },
    },
];

const marketplaceFeatures: Feature[] = [
    {
        title: 'Find Products',
        description: 'Search power system products from leading manufacturers, all in one place.',
        icon: <IoSearch />,
        preview: { type: 'image', image: productCatalogImage },
    },
    {
        title: 'Find Service Providers',
        description: 'Connect with manufacturers, integrators, and installers specializing in power systems.',
        icon: <IoBuildOutline />,
        preview: { type: 'image', image: professionalsImage },
    },
    {
        title: 'Company Profiles',
        description: 'Showcase your products, partnerships, and expertise with a rich company profile.',
        icon: <IoBusinessOutline />,
        preview: { type: 'image', image: profileImage },
    },
];

const Homepage = () => {
    const user = useCurrentUser();

    if (user) {
        return <Dashboard />;
    }

    return <MarketingHomepage />;
};

const MarketingHomepage = () => {
    const { image: heroImage, video: heroVideo } = useHeroMedia();

    return (
        <Page showBackground>
            <Hero image={heroVideo ? undefined : heroImage} video={heroVideo} body={<HeroCard />} />

            <FeatureShowcase
                sectionProps={{
                    'data-with-bg': 'grid',
                }}
                title={
                    <>
                        <Box component={Link} c="black" href={MarketingHelpers.urls.dcide.overview()}>
                            <DCIDE height={40} />
                        </Box>
                        <br />
                        <span className="gradient-cyan-green">Design DC Power Systems</span>
                        <br /> with Experts by Your Side
                    </>
                }
                description="From first sketch to full topology - prototype, simulate, and validate with real engineering models."
                visualPosition="right"
                footer={
                    <MoreInfoButton href={MarketingHelpers.urls.dcide.overview()}>
                        More about Design Software
                    </MoreInfoButton>
                }
            >
                {[features.inAppSupport, features.prototyping, features.inAppCommunication, features.faultSimulation]}
            </FeatureShowcase>

            <FeatureShowcase
                sectionProps={{
                    variant: 'light',
                    bgGradient: { show: true, position: ['50%', '0%'] },
                }}
                title={
                    <>
                        <Box component={Link} c="black" href={MarketingHelpers.urls.controller.overview()}>
                            <DCIDEController height={60} />
                        </Box>
                        <br />
                        <span className="gradient-cyan-green">Accelerate Deployment</span>
                        <br /> of DC Microgrids
                    </>
                }
                description="Plug-and-play automation for DC microgrids - connect any hardware, run smart energy algorithms, and monitor everything in real time."
                visualPosition="left"
                footer={
                    <MoreInfoButton href={MarketingHelpers.urls.controller.overview()}>
                        More about Controller
                    </MoreInfoButton>
                }
            >
                {controllerFeatures}
            </FeatureShowcase>

            <FeatureShowcase
                sectionProps={{
                    'data-with-bg': 'grid',
                    bgGradient: { show: true, position: ['50%', '0%'] },
                }}
                title={
                    <>
                        The Power Systems <span className="gradient-cyan-green">Marketplace</span>
                    </>
                }
                description="Find products, professionals, and partners - everything you need to design and deploy your next power system project."
                visualPosition="right"
                footer={
                    <Group justify="center">
                        <MoreInfoButton href={RouterHelpers.urls.products.overview()} leftSection={<IoSearch />}>
                            Explore Products
                        </MoreInfoButton>
                        <MoreInfoButton
                            href={RouterHelpers.urls.profiles.overview()}
                            leftSection={<IoBusinessOutline />}
                        >
                            Explore Professionals
                        </MoreInfoButton>
                    </Group>
                }
            >
                {marketplaceFeatures}
            </FeatureShowcase>

            {/* <Page.WideContent>
                <BrowseAllCategories />
            </Page.WideContent> */}
        </Page>
    );
};

export { Homepage };
